/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import {
  CellType,
  GridComponentType,
  IMediaListModel,
  IMediaModel,
  SourceType,
  ThemeContext,
} from "@nf/common";
import * as React from "react";
import dynamic from "next/dynamic";
import { ROUTES } from "@nf/constants";

import isEqual from "react-fast-compare";
import { WithTranslation } from "react-i18next";
import { Action, ActionCreator } from "redux";
import { useGridtemCount, useGridPaddingCount } from "./hooks";
import { IListComponentProps } from "../../types";
import styles from "./GridComponent.module.scss";

const ListComponentItem = dynamic(
  () =>
    import(
      "components/Configuration/ListComponent/components/ListComponentItem/components/ListComponentItem"
    )
);

const LinkWithLocale = dynamic(() => import("components/LinkWithLocale"));

const Chevron = "/assets/chevron.png";

const GRID_COLS = 3;
const GRID_ROWS = 3;
const DEFAULT_COL_PADDING = 8;
const DEFAULT_ROW_PADDING = 8;

export interface IGridComponentStateProps {
  source?: any;
}

export interface IGridComponentDispatchProps {
  getDataSourceCancel?: ActionCreator<Action>;
  getDataSource?: ActionCreator<Action>;
}

export interface IGridComponentOwnProps extends IListComponentProps {
  columns?: number;
  rows?: number;
  colPadding?: number;
  rowPadding?: number;
  gridComponentType?: GridComponentType;
  className?: string;
  style?: React.CSSProperties;
  dynamicHeight?: boolean;
  dataLength?: number;
  noAutoColumns?: boolean;
}
export interface IGridComponentProps
  extends IGridComponentStateProps,
    IGridComponentDispatchProps,
    IGridComponentOwnProps,
    WithTranslation {}

export const GridComponent = React.memo((props: IGridComponentProps) => {
  const autoColumns = useGridtemCount(props.columns, props.component.CellType);
  const columns = props.noAutoColumns ? props.columns : autoColumns;
  const gridPadding = useGridPaddingCount(props.gridComponentType); //adjust row + col grid gap

  if (props.dynamicHeight && props.dataLength && columns) {
    return (
      <GridComponentClass
        {...props}
        columns={columns}
        colPadding={gridPadding.col}
        rowPadding={gridPadding.row}
        rows={Math.ceil(props.dataLength / columns)}
      />
    );
  }
  return (
    <GridComponentClass
      {...props}
      columns={columns}
      colPadding={gridPadding.col}
      rowPadding={gridPadding.row}
    />
  );
});

export class GridComponentClass extends React.Component<IGridComponentProps> {
  static contextType = ThemeContext;

  public static defaultProps = {
    columns: GRID_COLS,
    rows: GRID_ROWS,
  };

  componentDidMount = () => {
    const { component, getDataSource } = this.props;

    if (component.SourceId && getDataSource) {
      getDataSource(component.SourceId);
    }
  };

  public shouldComponentUpdate(nextProps: IGridComponentProps) {
    return !isEqual(nextProps, this.props);
  }

  chunk = (array: IMediaModel[] | undefined, size = 1) => {
    if (!array) {
      return [];
    }

    const arrayChunks = [];
    for (let i = 0; i < array.length; i += size) {
      const arrayChunk = array.slice(i, i + size);
      arrayChunks.push(arrayChunk);
    }
    return arrayChunks;
  };

  private getSource = (): IMediaListModel | undefined => {
    const { component, source } = this.props;

    if (component.SourceId && source) {
      return source;
    } else if (component.MediaList) {
      return {
        SourceType: SourceType.MediaList,
        Entities: component.MediaList,
        TotalCount: component.MediaList.length,
        IsLoading: false,
      };
    }

    return undefined;
  };

  private renderItem = (media: IMediaModel) => {
    const { component, columns } = this.props;

    switch (component.CellType) {
      case CellType.Custom:
      case CellType.Cover:
      case CellType.CoverLarge:
      case CellType.Highlights:
      case CellType.Frame:
      case CellType.FrameLarge:
      default:
        return (
          <ListComponentItem
            media={media}
            cellType={component.CellType}
            cutomListItem={component.CutomListItem}
            isPlaceholder={!!this.loading}
            readOnly={this.loading}
            gridColumns={columns}
            query={component.QueryParams}
            style={{
              fontSize: `${
                this.context.themeProvider.getFontSize() *
                this.context.themeProvider.getListItemCountFactor(columns)
              }px`,
            }}
          />
        );
    }
  };

  get loading() {
    const { loading } = this.props;
    const source = this.getSource();

    return loading || source?.IsLoading;
  }

  renderHeader = () => {
    const { component, readOnly, t } = this.props;
    const source = this.getSource();
    const title = t(component.TitleTranslationKey || "", component.Title);

    const hasRedirectLink = !readOnly && component.SourceId;
    const hasItems = source?.Entities;

    if (title || component.SourceId) {
      return (
        <header className={styles.titleContainer}>
          <p className={styles.title}>
            {hasItems && !this.loading ? title : ""}
          </p>

          {hasItems && hasRedirectLink && !this.loading && (
            <LinkWithLocale
              href={`${ROUTES.PLAYLIST_SCREEN}/${component.SourceId}`}
            >
              <a className={styles.seeAll}>
                {t("web_browse_content_see_all")}
                {/* FIXME replace with svg icon */}
                <img src={Chevron} alt="" />
              </a>
            </LinkWithLocale>
          )}
        </header>
      );
    }
  };

  render() {
    const {
      className = styles.grid,
      columns = GRID_COLS,
      rows = GRID_ROWS,
      rowPadding = DEFAULT_ROW_PADDING,
      colPadding = DEFAULT_COL_PADDING,
      style,
    } = this.props;

    const source = this.getSource();
    let entities = source?.Entities;

    if (!entities?.length && this.loading) {
      entities = [...Array(columns * rows).keys()].map(() => ({ Id: -1 }));
    }

    let grid = this.chunk(entities, columns);

    if (rows) {
      grid = grid.slice(0, rows);
    }

    return (
      <div className={className} style={style}>
        {this.renderHeader()}
        {grid.map((row, rowIndex) => (
          <div
            key={`row-${rowIndex}`}
            className={styles.row}
            style={{ margin: `0 -${rowPadding / 2}px ${rowPadding}px` }}
          >
            {row.map((column, columnIndex) => (
              <div
                key={`col-${columnIndex}`}
                className={styles.col}
                style={{
                  width: ` ${100 / columns}%`,
                  padding: `0 ${colPadding / 2}px`,
                }}
              >
                {this.renderItem(column)}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }
}

export default GridComponent;
